/**
 * Multi-place sections: one section, several shapes.
 *
 * A GA zone is often the same pool of standing room spread over several places
 * in a room (both wings of a balcony, three pockets around a pit). Before this,
 * each place had to be its own section, which meant its own capacity, its own
 * availability and its own highlight — the buyer saw three zones where the
 * venue sells one.
 *
 * A section now carries optional extra `parts`. The section's own geometry is
 * part one and the extras follow, so a chart saved before this change reads
 * back as a one-part section and every caller below behaves exactly as it did.
 *
 * ONE RULE: identity lives on the SECTION, never on a part. Capacity, category,
 * price, availability, the cart line and the highlight are all keyed by the
 * section — parts only say where to draw it. Nothing here touches inventory.
 */
import type { LayoutSection, SectionShapePart } from '@/types/seating'
import { flattenPolygonOutline } from './section-geometry'

/** The geometry fields a single drawable piece carries. */
export interface SectionPart {
  shape?: string
  x: number
  y: number
  width: number
  height: number
  rotation?: number
  points?: number[][]
  edgeCurves?: number[]
  edgeSkew?: number[]
  arcConfig?: {
    cx: number
    cy: number
    innerRadius: number
    outerRadius: number
    startAngle: number
    endAngle: number
  }
}

/** What `sectionParts` accepts: LayoutSection, DesignerSection, or any shape-alike. */
export type PartSource = SectionPart & { parts?: SectionShapePart[] | null }

export interface Bounds {
  x: number
  y: number
  w: number
  h: number
}

function rotatePoint(px: number, py: number, cx: number, cy: number, deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  const dx = px - cx
  const dy = py - cy
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos]
}

/**
 * Every drawable piece of a section, the section's own shape first.
 * Callers loop this instead of reading `section.points` directly.
 */
export function sectionParts(section: PartSource): SectionPart[] {
  const primary: SectionPart = {
    shape: section.shape,
    x: section.x,
    y: section.y,
    width: section.width,
    height: section.height,
    rotation: section.rotation,
    points: section.points,
    edgeCurves: section.edgeCurves,
    edgeSkew: section.edgeSkew,
    arcConfig: section.arcConfig,
  }
  const extra = section.parts
  if (!extra || extra.length === 0) return [primary]
  return [primary, ...extra]
}

/** True when the section is drawn in more than one place. */
export function hasMultipleParts(section: PartSource): boolean {
  return !!section.parts && section.parts.length > 0
}

/** Bounding box of ONE part, honouring arcs, bowed polygon edges and rotation. */
export function partBounds(part: SectionPart): Bounds {
  if (part.shape === 'arc' && part.arcConfig) {
    const arc = part.arcConfig
    const pts: [number, number][] = []
    for (let deg = arc.startAngle; deg <= arc.endAngle; deg += 2) {
      const rad = (deg * Math.PI) / 180
      pts.push([arc.cx + arc.outerRadius * Math.cos(rad), arc.cy + arc.outerRadius * Math.sin(rad)])
      pts.push([arc.cx + arc.innerRadius * Math.cos(rad), arc.cy + arc.innerRadius * Math.sin(rad)])
    }
    const endRad = (arc.endAngle * Math.PI) / 180
    pts.push([arc.cx + arc.outerRadius * Math.cos(endRad), arc.cy + arc.outerRadius * Math.sin(endRad)])
    if (pts.length === 0) return { x: part.x, y: part.y, w: part.width, h: part.height }
    const xs = pts.map(p => p[0])
    const ys = pts.map(p => p[1])
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) }
  }

  if (part.shape === 'polygon' && part.points && part.points.length >= 3) {
    // Bounds of the DRAWN outline — a bowed edge bulges past its straight chord.
    let pts = flattenPolygonOutline(part.points, part.edgeCurves, part.edgeSkew)
    if (part.rotation) {
      const cx = (Math.min(...part.points.map(p => p[0])) + Math.max(...part.points.map(p => p[0]))) / 2
      const cy = (Math.min(...part.points.map(p => p[1])) + Math.max(...part.points.map(p => p[1]))) / 2
      pts = pts.map(p => rotatePoint(p[0], p[1], cx, cy, part.rotation!))
    }
    const xs = pts.map(p => p[0])
    const ys = pts.map(p => p[1])
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) }
  }

  if (part.rotation) {
    const cx = part.x + part.width / 2
    const cy = part.y + part.height / 2
    const corners: [number, number][] = [
      [part.x, part.y],
      [part.x + part.width, part.y],
      [part.x + part.width, part.y + part.height],
      [part.x, part.y + part.height],
    ]
    const rotated = corners.map(([px, py]) => rotatePoint(px, py, cx, cy, part.rotation!))
    const xs = rotated.map(p => p[0])
    const ys = rotated.map(p => p[1])
    return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) }
  }

  return { x: part.x, y: part.y, w: part.width, h: part.height }
}

/** Union bounds across every part — what viewport culling and zoom-to-section want. */
export function sectionBounds(section: PartSource): Bounds {
  const boxes = sectionParts(section).map(partBounds)
  const minX = Math.min(...boxes.map(b => b.x))
  const minY = Math.min(...boxes.map(b => b.y))
  const maxX = Math.max(...boxes.map(b => b.x + b.w))
  const maxY = Math.max(...boxes.map(b => b.y + b.h))
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

/** The part with the largest area — where a multi-place section's label belongs. */
export function largestPart(section: PartSource): SectionPart {
  const parts = sectionParts(section)
  let best = parts[0]
  let bestArea = -1
  for (const part of parts) {
    const b = partBounds(part)
    const area = b.w * b.h
    if (area > bestArea) {
      bestArea = area
      best = part
    }
  }
  return best
}

/** Centre of a part: an arc uses its mid-angle, everything else its bounds. */
export function partCenter(part: SectionPart): { cx: number; cy: number } {
  if (part.shape === 'arc' && part.arcConfig) {
    const a = part.arcConfig
    const midAngle = (((a.startAngle + a.endAngle) / 2) * Math.PI) / 180
    const midR = (a.innerRadius + a.outerRadius) / 2
    return { cx: a.cx + midR * Math.cos(midAngle), cy: a.cy + midR * Math.sin(midAngle) }
  }
  const b = partBounds(part)
  return { cx: b.x + b.w / 2, cy: b.y + b.h / 2 }
}

function pointInPolygon(x: number, y: number, poly: number[][]): boolean {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0]
    const yi = poly[i][1]
    const xj = poly[j][0]
    const yj = poly[j][1]
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

function pointInArc(x: number, y: number, arc: NonNullable<SectionPart['arcConfig']>): boolean {
  const dx = x - arc.cx
  const dy = y - arc.cy
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist < arc.innerRadius || dist > arc.outerRadius) return false
  let angle = (Math.atan2(dy, dx) * 180) / Math.PI
  let start = arc.startAngle
  let end = arc.endAngle
  while (angle < start) angle += 360
  while (angle > start + 360) angle -= 360
  if (end < start) end += 360
  return angle >= start && angle <= end
}

/** Is the point inside THIS piece? */
export function pointInPart(x: number, y: number, part: SectionPart): boolean {
  if (part.shape === 'arc' && part.arcConfig) return pointInArc(x, y, part.arcConfig)

  if (part.shape === 'polygon' && part.points && part.points.length >= 3) {
    let testX = x
    let testY = y
    if (part.rotation) {
      const pts = part.points
      const pcx = (Math.min(...pts.map(p => p[0])) + Math.max(...pts.map(p => p[0]))) / 2
      const pcy = (Math.min(...pts.map(p => p[1])) + Math.max(...pts.map(p => p[1]))) / 2
      const [rx, ry] = rotatePoint(x, y, pcx, pcy, -part.rotation)
      testX = rx
      testY = ry
    }
    return pointInPolygon(testX, testY, flattenPolygonOutline(part.points, part.edgeCurves, part.edgeSkew))
  }

  let testX = x
  let testY = y
  if (part.rotation) {
    const [rx, ry] = rotatePoint(x, y, part.x + part.width / 2, part.y + part.height / 2, -part.rotation)
    testX = rx
    testY = ry
  }
  return testX >= part.x && testX <= part.x + part.width && testY >= part.y && testY <= part.y + part.height
}

/** Is the point inside ANY piece of the section? Clicking any place selects the zone. */
export function pointInSection(x: number, y: number, section: PartSource): boolean {
  return sectionParts(section).some(part => pointInPart(x, y, part))
}

/** Shift every piece of a section by (dx, dy). Used by the designer's drag. */
export function translateParts(parts: SectionShapePart[] | undefined, dx: number, dy: number): SectionShapePart[] | undefined {
  if (!parts || parts.length === 0) return parts
  return parts.map(part => ({
    ...part,
    x: part.x + dx,
    y: part.y + dy,
    points: part.points ? part.points.map(p => [p[0] + dx, p[1] + dy]) : part.points,
    arcConfig: part.arcConfig ? { ...part.arcConfig, cx: part.arcConfig.cx + dx, cy: part.arcConfig.cy + dy } : part.arcConfig,
  }))
}

/** Take a whole section's geometry and turn it into a part (used when merging). */
export function partFromSection(section: PartSource): SectionShapePart {
  return {
    shape: (section.shape as SectionShapePart['shape']) || 'rect',
    x: section.x,
    y: section.y,
    width: section.width,
    height: section.height,
    ...(section.rotation ? { rotation: section.rotation } : {}),
    ...(section.points ? { points: section.points } : {}),
    ...(section.edgeCurves ? { edgeCurves: section.edgeCurves } : {}),
    ...(section.edgeSkew ? { edgeSkew: section.edgeSkew } : {}),
    ...(section.arcConfig ? { arcConfig: section.arcConfig } : {}),
  }
}

/** Sanity check for a stored layout: parts must carry real geometry. */
export function isDrawablePart(part: SectionPart): boolean {
  if (part.shape === 'arc') return !!part.arcConfig
  if (part.shape === 'polygon') return !!part.points && part.points.length >= 3
  return Number.isFinite(part.x) && Number.isFinite(part.y) && part.width > 0 && part.height > 0
}

export type { LayoutSection }
